// ════════════════════════════════════════════════════════════
//  online/mind.js — THE MIND: how the League's NPCs learn and adapt
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "I want the League to be able to learn and adapt. If
//  one winner realizes some stuff was working they keep picking that same
//  strategy etc maybe loser are losing cause they're experimenting and don't
//  like the 'meta'" … "maybe some just want a being because they like it more
//  than the rest, meta/winning/losing has nothing to do with it … Maybe
//  another feels that way but decides at some point one being can be
//  switched cause they just want to win now".
//
//  ── WHAT AN NPC HAS ─────────────────────────────────────────
//   · a PLAYBOOK — how it fights (a battle.js STYLE) and its three beings.
//   · a TEMPER (fixed, who they are) — TEMPERS below: how many losses in a
//     row before it changes, how often it fiddles anyway, and how it feels
//     about the meta (chases it / ignores it / refuses it).
//   · LOVES (fixed until they break) — beings it won't drop, its whole trio,
//     or the way it fights. `until` = the losing streak that breaks one:
//     it swaps ONE thing to win, once ("heartbreak"), and lives with it.
//   · a MEMORY — what has worked for it (styles and beings, recent fights
//     count more), and its streak.
//  Who each NPC is: data/leagueMinds.json (Claude's placeholders).
//
//  ── HOW IT STAYS CANON (ORDER IS THE LAW, ledger.js) ────────
//   The mind is never stored live. It is a FOLD over the ledger rows — the
//   same rows on every screen give the same minds. Week boundaries carry it
//   like the ratings do (weeks/{w}/league.minds). An hour's choices are made
//   from every row BEFORE it, written into that hour's lock (lock.pb), and
//   every fight row records the playbook used (sa/sb · ta/tb · ca/cb).
//   Paper fights (the fast-forward) move morale (streaks) but teach nothing
//   about what works — a paper result never saw a style.
//
//  This file is PURE (no Firebase, no clock). Any game's League can use it:
//  the game-specific part is the import block (styles, beings, team size).
// ════════════════════════════════════════════════════════════
import { STYLES, STYLE_IDS, TEAM_SIZE } from "../sim/battle.js";
import { LINE_IDS, LINES } from "../data/roster.js";
import { teamFromNames } from "./rooms.js";
import { BEINGS, mulberry, strSeed, cleanRows } from "./circuit.js";
import MINDS from "../data/leagueMinds.json";

// ── DIALS ────────────────────────────────────────────────────
// patience = losses in a row (since its last change) before it changes something
// tinker   = chance, each hour it fights, to try something new anyway
// meta     = +1 chases what's winning · 0 ignores it · −1 refuses what everyone's using
// noise    = how much whim goes into what it picks
export const TEMPERS = {
  loyalist: { name: "Loyalist", blurb: "found what works, rides it", patience: 5, tinker: 0.01, meta: 0, noise: 0.15 },
  tinkerer: { name: "Tinkerer", blurb: "always trying something new", patience: 3, tinker: 0.08, meta: 0, noise: 0.5 },
  rebel: { name: "Rebel", blurb: "won't touch what everyone's using", patience: 4, tinker: 0.05, meta: -1, noise: 0.3 },
  copycat: { name: "Copycat", blurb: "chases whatever's hot", patience: 3, tinker: 0.03, meta: 1, noise: 0.15 },
  competitor: { name: "Competitor", blurb: "whatever wins", patience: 3, tinker: 0.03, meta: 0.5, noise: 0.1 },
};
export const MEM_DECAY = 0.93;    // ⟵ DIAL — its own memory, per real fight it has (older fights fade)
export const META_DECAY = 0.998;  // ⟵ DIAL — the League's memory, per real fight anywhere (~a day to halve)
export const CHANGES_KEPT = 6;    // recent changes remembered per NPC (for its page)
export const COPY_CHANCE = 0.15;  // a copycat off the hot style, each hour: chance it switches to it
export const REBEL_CHANCE = 0.5;  // a rebel on a POPULAR style, each hour: chance it walks away
export const REBEL_SHARE = 0.28;  // ⟵ DIAL — a style this much of the League uses is "popular" (a fair share is 20%)
// why a playbook changed (row ca/cb, lock pb.c)
export const WHY = {
  slump: "losing streak — trying something new",
  tinker: "just felt like trying something",
  meta: "jumped on what's winning",
  rebel: "too many people doing it — moved on",
  hb: "heartbreak — let go of a favourite to win",
};

const r3 = (x) => Math.round(x * 1000) / 1000;
const rate = (rec) => (rec ? (rec[0] + 1) / (rec[0] + rec[1] + 2) : 0.5);   // win rate with a prior: no fights reads 50%
const lineName = (l) => (LINES[l] ? LINES[l].name : l);
export const styleName = (s) => (STYLES[s] ? STYLES[s].name : "Balanced");

/** Who an NPC is (the fixed part), from data/leagueMinds.json. Unknown keys: a plain competitor. */
export function cfgOf(key) {
  const raw = MINDS[key] || {};
  const team = (BEINGS.get(key) || {}).team || [];
  const loves = raw.loves === "trio" ? team.slice() : Array.isArray(raw.loves) ? raw.loves.flatMap((n) => teamFromNames(n)) : [];
  return {
    temper: TEMPERS[raw.temper] ? raw.temper : "competitor",
    style: STYLES[raw.style] ? raw.style : "balanced",
    loves, trio: raw.loves === "trio", lovesStyle: !!raw.lovesStyle,
    until: Number.isFinite(raw.until) && raw.until > 0 ? raw.until : null,
  };
}

// ════════════════════════════════════════════════════════════
//  THE STATE — { n: { key: npc }, m: the League's memory }
//   npc = { s style, t [lines], st streak (+W/−L), lc losses since its last change, fs fights since it,
//           love [lines], ls loves its style, hb heartbroken (0/1),
//           mem { s:{style:[w,l]}, b:{line:[w,l]} }, ch [{ h, c, k:"style"|"being", from, to }], since h }
//   m   = { s:{style:[w,l]}, b:{line:[w,l]} }   (real fights only, fading)
//  JSON-safe and small: it is stored with each week's leagues.
// ════════════════════════════════════════════════════════════
export function genesisMinds(keys = [...BEINGS.keys()]) {
  const n = {};
  for (const k of keys) {
    const c = cfgOf(k), team = ((BEINGS.get(k) || {}).team || []).slice(0, TEAM_SIZE);
    n[k] = { s: c.style, t: team, st: 0, lc: 0, fs: 0, love: c.loves.filter((l) => team.includes(l)), ls: c.lovesStyle ? 1 : 0, hb: 0, mem: { s: {}, b: {} }, ch: [], since: null };
  }
  return { n, m: { s: {}, b: {} } };
}
// ONE SHAPE: a mind read back from Firebase has lost its empty parts and comes back with
// its keys sorted. Every mind is rebuilt in one canonical shape, so a fresh screen and a
// long-running one hold byte-identical minds (and so decide identically).
const arr = (v) => (Array.isArray(v) ? v.slice() : []);
const num = (v) => (Number.isFinite(v) ? v : 0);
const table = (t) => Object.fromEntries(Object.keys(t || {}).sort().map((id) => [id, [num(t[id][0]), num(t[id][1])]]));
function canonNpc(x) {
  return {
    s: x.s, t: arr(x.t), st: num(x.st), lc: num(x.lc), fs: num(x.fs), love: arr(x.love), ls: num(x.ls), hb: num(x.hb),
    mem: { s: table(x.mem && x.mem.s), b: table(x.mem && x.mem.b) },
    ch: arr(x.ch).map((c) => ({ h: c.h, c: c.c, k: c.k, from: c.from ?? null, to: c.to ?? null })),
    since: Number.isFinite(x.since) ? x.since : null,
  };
}
function npcOf(S, k) {
  if (!S.n[k]) S.n[k] = genesisMinds([k]).n[k];
  return S.n[k];
}
function fade(tbl, k) { for (const id of Object.keys(tbl)) { const v = tbl[id]; tbl[id] = [r3(v[0] * k), r3(v[1] * k)]; } }
function add(tbl, id, won) { const v = tbl[id] || [0, 0]; tbl[id] = won ? [r3(v[0] + 1), v[1]] : [v[0], r3(v[1] + 1)]; }

/** A row's playbook for side 0/1 (rows from before the Mind carry none → whatever the NPC had). */
export const rowBook = (row, side) => ({
  s: side ? row.sb : row.sa, t: side ? row.tb : row.ta, c: side ? row.cb : row.ca,
});

/**
 * Fold ledger rows into the minds. Returns a NEW state (the input is untouched).
 * Rows are applied in bell order (cleanRows), so every screen gets the same minds.
 */
export function foldMinds(start, rows) {
  const src = start || genesisMinds();
  const S = { n: Object.fromEntries(Object.keys(src.n || {}).sort().map((k) => [k, canonNpc(src.n[k])])), m: { s: table(src.m && src.m.s), b: table(src.m && src.m.b) } };
  for (const row of cleanRows(rows)) {
    const real = row.why !== "paper";
    if (real) { fade(S.m.s, META_DECAY); fade(S.m.b, META_DECAY); }
    [row.a, row.b].forEach((k, side) => {
      if (!BEINGS.has(k)) return;   // a CHALLENGER (a player or partner) has no mind here — they think for themselves
      const x = npcOf(S, k), won = row.w === side, bk = rowBook(row, side);
      // 1 · the playbook it actually used (a change shows up here, the hour it happened)
      const s = STYLES[bk.s] ? bk.s : x.s;
      const t = Array.isArray(bk.t) && bk.t.length === TEAM_SIZE ? bk.t : x.t;
      if (s !== x.s || t.join() !== x.t.join()) {
        if (s !== x.s) x.ch.push({ h: row.h, c: bk.c || "tinker", k: "style", from: x.s, to: s });
        const out = x.t.filter((l) => !t.includes(l)), inn = t.filter((l) => !x.t.includes(l));
        out.forEach((l, i) => x.ch.push({ h: row.h, c: bk.c || "tinker", k: "being", from: l, to: inn[i] || null }));
        if (bk.c === "hb") {   // heartbreak: the love that gave way is gone for good
          x.love = x.love.filter((l) => t.includes(l));
          if (s !== x.s) x.ls = 0;
          x.hb = 1;
        }
        while (x.ch.length > CHANGES_KEPT) x.ch.shift();
        x.s = s; x.t = t.slice(); x.lc = 0; x.fs = 0; x.since = row.h;
      }
      // 2 · morale (every fight, paper too)
      x.st = won ? (x.st > 0 ? x.st + 1 : 1) : (x.st < 0 ? x.st - 1 : -1);
      x.lc = won ? 0 : x.lc + 1;
      x.fs = (x.fs || 0) + 1;   // fights since its last change
      // 3 · what works (real fights only)
      if (real) {
        fade(x.mem.s, MEM_DECAY); fade(x.mem.b, MEM_DECAY);
        add(x.mem.s, s, won); t.forEach((l) => add(x.mem.b, l, won));
        add(S.m.s, s, won); t.forEach((l) => add(S.m.b, l, won));
      }
    });
  }
  return { n: Object.fromEntries(Object.keys(S.n).sort().map((k) => [k, canonNpc(S.n[k])])), m: { s: table(S.m.s), b: table(S.m.b) } };
}

// ════════════════════════════════════════════════════════════
//  THE META — what the League is using, and how it's going
// ════════════════════════════════════════════════════════════
const sum = (v) => (v ? v[0] + v[1] : 0);
/** Share of use and win rate per id, from a { id:[w,l] } table. */
function shares(tbl, ids, per = 1) {
  const total = ids.reduce((a, id) => a + sum(tbl[id]), 0) || 1;
  return Object.fromEntries(ids.map((id) => [id, { share: sum(tbl[id]) / total * per, win: rate(tbl[id]) }]));
}
/** The meta as the minds see it: { styles:{id:{share,win}}, beings:{line:{share,win}}, mostUsed, hottest }. */
export function metaOf(S) {
  const styles = shares(S.m.s, STYLE_IDS), beings = shares(S.m.b, LINE_IDS, TEAM_SIZE);
  const used = STYLE_IDS.filter((id) => sum(S.m.s[id]) > 0);
  const mostUsed = used.slice().sort((a, b) => styles[b].share - styles[a].share || (a < b ? -1 : 1))[0] || null;
  // hottest: best win rate among styles that are really being played (≥ half a fair share)
  const played = used.filter((id) => styles[id].share >= 0.5 / STYLE_IDS.length);
  const hottest = played.sort((a, b) => styles[b].win - styles[a].win || (a < b ? -1 : 1))[0] || null;
  return { styles, beings, mostUsed, hottest };
}

// ════════════════════════════════════════════════════════════
//  DECIDE — what an NPC fights with in hour h
// ════════════════════════════════════════════════════════════
/**
 * Its playbook for hour h, from the minds as they stand going in (everything before h).
 * Resolves { s, t, c } — c = why it changed (a WHY key), or null if it kept what it had.
 * Seeded by key + hour: the same answer on every screen.
 */
export function decide(S, key, h, meta = metaOf(S)) {
  const x = canonNpc(S.n[key] || genesisMinds([key]).n[key]);
  const cfg = cfgOf(key), T = TEMPERS[cfg.temper];
  const R = mulberry(strSeed(`mind:${key}:${h}`));
  const keep = { s: x.s, t: x.t.slice(), c: null };
  const roll = R(), roll2 = R();
  // WHY would it change?
  let c = null;
  const loved = x.love.length || x.ls;
  if (cfg.until && !x.hb && loved && x.st <= -cfg.until) c = "hb";
  else if (x.lc >= T.patience) c = "slump";
  else if (roll < T.tinker) c = "tinker";
  else if (T.meta > 0 && meta.hottest && x.s !== meta.hottest && !x.ls && roll2 < COPY_CHANCE * T.meta) c = "meta";
  else if (T.meta < 0 && !x.ls && (x.s === meta.mostUsed || (meta.styles[x.s] && meta.styles[x.s].share >= REBEL_SHARE)) && roll2 < REBEL_CHANCE * -T.meta) c = "rebel";
  if (!c) return keep;

  // WHAT can it change?
  const lockedB = c === "hb" ? [] : x.love;                        // heartbreak: a loved being may go (one)
  const styleOk = c === "hb" ? (x.ls ? true : !x.love.length) : !x.ls;
  const freeB = x.t.filter((l) => !lockedB.includes(l));
  const beingOk = freeB.length > 0 && (c !== "hb" || x.love.length > 0) && c !== "meta" && c !== "rebel";
  let what = null;
  if (c === "hb") what = x.ls ? "style" : "being";
  else if (c === "meta" || c === "rebel") what = styleOk ? "style" : null;
  else if (styleOk && beingOk) what = R() < 0.5 ? "style" : "being";
  else what = styleOk ? "style" : beingOk ? "being" : null;
  if (!what) return keep;   // nothing it's willing to touch — riding it out

  // how much it likes an option: its own record + how it feels about the meta + a little whim
  const score = (own, m) => {
    let v = rate(own);
    if (T.meta > 0) v += T.meta * ((m.win - 0.5) * 2 + 0.5 * m.share);
    else if (T.meta < 0) v += T.meta * m.share * 2;
    return v + T.noise * (R() - 0.5);
  };
  const best = (ids, own, met) => ids.map((id) => ({ id, v: score(own[id], met[id] || { win: 0.5, share: 0 }) }))
    .sort((a, b) => b.v - a.v || (a.id < b.id ? -1 : 1))[0];

  if (what === "style") {
    const pick = best(STYLE_IDS.filter((id) => id !== x.s && (c !== "meta" || id === meta.hottest)), x.mem.s, meta.styles);
    return pick ? { s: pick.id, t: x.t.slice(), c } : keep;
  }
  // a being: drop the one doing worst for it (among those it'll part with), bring in its favourite pick
  const pool = c === "hb" ? x.love.filter((l) => x.t.includes(l)) : freeB;
  const drop = pool.map((l) => ({ l, v: rate(x.mem.b[l]) + 0.05 * (R() - 0.5) })).sort((a, b) => a.v - b.v || (a.l < b.l ? -1 : 1))[0];
  const inn = drop && best(LINE_IDS.filter((l) => !x.t.includes(l)), x.mem.b, meta.beings);
  if (!drop || !inn) return keep;
  return { s: x.s, t: x.t.map((l) => (l === drop.l ? inn.id : l)), c };
}

/** Every listed NPC's playbook for hour h: { key: { s, t, c } } (c only when it changed). */
export function decideHour(S, keys, h) {
  const meta = metaOf(S), out = {};
  for (const k of [...new Set(keys)].filter((k) => k && BEINGS.has(k)).sort()) {   // NPCs only: a Challenger picks their own three
    const d = decide(S, k, h, meta);
    out[k] = d.c ? d : { s: d.s, t: d.t };
  }
  return out;
}

// ════════════════════════════════════════════════════════════
//  FOR THE SCREENS (pure)
// ════════════════════════════════════════════════════════════
/** A change, in a few friendly words: "swapped Z4Z for DG-0" · "switched to Artist". */
export function changeText(ch) {
  if (ch.k === "style") return `switched to ${styleName(ch.to)}`;
  return ch.to ? `swapped ${lineName(ch.from)} for ${lineName(ch.to)}` : `dropped ${lineName(ch.from)}`;
}
/** One NPC, for its page: who they are, how they fight now, how it's going. */
export function mindView(S, key) {
  const x = canonNpc(S && S.n && S.n[key] ? S.n[key] : genesisMinds([key]).n[key]);
  const cfg = cfgOf(key), T = TEMPERS[cfg.temper];
  const loves = [...(cfg.trio && !x.hb ? ["their whole team"] : x.love.map(lineName)), ...(x.ls ? [`fighting as a ${styleName(x.s)}`] : [])];
  const last = x.ch[x.ch.length - 1] || null;
  let mood;
  if (x.hb && last && last.c === "hb") mood = "Let go of a favourite to win";
  else if (x.st >= 3) mood = "On a roll — not changing a thing";
  else if (x.lc >= T.patience - 1 && x.lc > 0) mood = "Feeling the pressure";
  else if (last && (x.fs || 0) <= 3) mood = "Trying something new";
  else mood = "Sticking with what they know";
  return {
    temper: T.name, temperBlurb: T.blurb, style: x.s, styleName: styleName(x.s), styleBlurb: (STYLES[x.s] || {}).blurb || "",
    team: x.t.slice(), loves, streak: x.st, mood,
    changes: x.ch.slice().reverse().map((ch) => ({ ...ch, text: changeText(ch), why: WHY[ch.c] || "" })),
  };
}

/**
 * The Meta board, straight off some rows (real fights with a playbook only):
 * { fights, styles:[{ id, name, uses, share, win }], beings:[{ line, name, picks, share, win }],
 *   trios:[{ t, uses, win }], changes:[{ key, h, text, why }] } — best-used first.
 */
export function metaBoard(rows) {
  const st = {}, be = {}, tr = {}, changes = [];
  let fights = 0;
  for (const row of cleanRows(rows)) {
    [0, 1].forEach((side) => {
      const bk = rowBook(row, side), k = side ? row.b : row.a, won = row.w === side;
      if (bk.c && !changes.some((c) => c.key === k && c.h === row.h)) changes.push({ key: k, h: row.h, c: bk.c, why: WHY[bk.c] || "" });
      if (row.why === "paper" || !STYLES[bk.s] || !Array.isArray(bk.t)) return;
      add(st, bk.s, won); bk.t.forEach((l) => add(be, l, won));
      const tk = bk.t.slice().sort().join("|"); add(tr, tk, won);
    });
    if (row.why !== "paper" && STYLES[row.sa]) fights++;
  }
  const list = (tbl, ids, per) => {
    const total = ids.reduce((a, id) => a + sum(tbl[id]), 0) || 1;
    return ids.map((id) => ({ id, uses: sum(tbl[id]), share: sum(tbl[id]) / total * per, win: tbl[id] ? tbl[id][0] / sum(tbl[id]) : null }))
      .sort((a, b) => b.uses - a.uses || (a.id < b.id ? -1 : 1));
  };
  return {
    fights,
    styles: list(st, STYLE_IDS, 1).map((r) => ({ ...r, name: styleName(r.id) })),
    beings: list(be, LINE_IDS, TEAM_SIZE).map((r) => ({ line: r.id, name: lineName(r.id), picks: r.uses, share: r.share, win: r.win })),
    trios: Object.keys(tr).map((id) => ({ t: id.split("|"), uses: sum(tr[id]), win: tr[id][0] / sum(tr[id]) })).sort((a, b) => b.uses - a.uses || b.win - a.win).slice(0, 5),
    changes: changes.reverse(),
  };
}
